// frontend/src/components/forms/CompetitionEntryForm.jsx

import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { createSubmission, getUploadUrls, deleteFile } from "@/services/apiService";
import { usePartnerAdmin } from "@/contexts/PartnerAdminContext";
import { useModal } from "@/contexts/ModalContext";
import { useAuth } from "@/contexts/AuthContext";
import ModalCard from "@/components/ui/ModalCard";
import { CloseIconButton } from "@/components/ui/button";
import MediaPreview from "@/components/ui/MediaPreview";
import UploadProgressBar from "@/components/ui/UploadProgressBar";

function getCsrfToken() {
  const name = "csrftoken";
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

const CompetitionEntryForm = () => {
  const { modalType, modalProps, closeModal } = useModal();
  const { user: authUser } = useAuth();

  const [files, setFiles] = useState([]);
  const [blobUrls, setBlobUrls] = useState({});
  const [description, setDescription] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const [isInvalid, setIsInvalid] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [fileProgress, setFileProgress] = useState({});

  const uploadProgress = (() => {
    const values = Object.values(fileProgress);
    if (values.length === 0) return 0;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  })();

const { membership } = usePartnerAdmin();

if (modalType !== "COMP_ENTRY_FORM") return null;

const { comp_id, competition, updateSubmissions } = modalProps || {};

const isTenantAdmin = membership?.role === "admin" || membership?.role === "manager";
const isInvitationOnly = competition?.submissions_restricted_to_admin && !isTenantAdmin;

if (isInvitationOnly) {
  return (
    <Dialog open onOpenChange={(open) => { if (!open) closeModal(); }}>
      <DialogContent className="flex justify-center items-center">
        <ModalCard title="Enter the Competition" onClose={() => closeModal()}>
          <div className="py-6 text-center space-y-3">
            <p className="text-sm font-medium text-swan">
              Submissions to this competition are by invitation only.
            </p>
            <p className="text-xs text-field-grey">
              Only designated users can submit entries.
            </p>
          </div>
        </ModalCard>
      </DialogContent>
    </Dialog>
  );
}
  const markLoadingForNewFiles = (startIndex, count) => {
    const next = {};
    for (let i = 0; i < count; i++) next[startIndex + i] = true;
    setLoadingStates((prev) => ({ ...prev, ...next }));
  };

  const handleMediaLoad = (index) => {
    if (loadingStates[index]) {
      setLoadingStates((prev) => ({ ...prev, [index]: false }));
    }
  };

  const addFiles = (incomingFiles) => {
    const list = Array.from(incomingFiles || []);
    const valid = list.filter(
      (file) => file?.type?.startsWith("image/") || file?.type?.startsWith("video/")
    );
    setIsInvalid(valid.length !== list.length);
    if (valid.length === 0) return;

    const startIndex = files.length;
    const newUrls = {};
    valid.forEach((f) => { newUrls[f.name] = URL.createObjectURL(f); });
    setFiles((prev) => [...prev, ...valid]);
    setBlobUrls((prev) => ({ ...prev, ...newUrls }));
    markLoadingForNewFiles(startIndex, valid.length);
  };

  const handleFileInput = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };

  const removeFile = (indexToRemove) => {
    const fileToRemove = files[indexToRemove];
    if (fileToRemove && blobUrls[fileToRemove.name]) {
      URL.revokeObjectURL(blobUrls[fileToRemove.name]);
      setBlobUrls((prev) => {
        const next = { ...prev };
        delete next[fileToRemove.name];
        return next;
      });
    }
    setFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
    setLoadingStates((prev) => {
      const next = {};
      const remainingCount = files.length - 1;
      for (let i = 0; i < remainingCount; i++) next[i] = false;
      return next;
    });
  };

  useEffect(() => {
    return () => {
      Object.values(blobUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [blobUrls]);

  const handleSubmit = async () => {
    setErrorMessage(null);
    setIsSuccess(false);
    setFileProgress({});

    if (!comp_id) {
      setErrorMessage("Missing competition id.");
      return;
    }

    if (files.length === 0 || !description.trim()) {
      setErrorMessage("Please add at least one file and a description.");
      return;
    }

    // ✅ Email verification gate — check BEFORE upload starts
    // Saves user from waiting for a full upload only to hit a 403
    if (!authUser?.email_verified) {
      setErrorMessage(
        "Your email isn't verified yet. Please check your inbox for the verification link before submitting."
      );
      return;
    }

    setIsUploading(true);

    const initialProgress = {};
    files.forEach((_, i) => { initialProgress[i] = 0; });
    setFileProgress(initialProgress);

    const files_metadata = [];

    try {
      const urlPairs = await Promise.all(files.map((file) => getUploadUrls(file)));

      const uploadPromises = files.map((file, index) => {
        const { upload_url, file_url } = urlPairs[index];

        files_metadata.push({
          file_url,
          file_type: file.type.split("/")[0],
        });

        const headers = { "Content-Type": file.type };
        if (import.meta.env.DEV) {
          const csrf = getCsrfToken();
          if (csrf) headers["X-CSRFToken"] = csrf;
        }

        return axios.put(upload_url, file, {
          headers,
          withCredentials: Boolean(import.meta.env.DEV),
          onUploadProgress: (event) => {
            if (event.total) {
              const pct = Math.round((event.loaded / event.total) * 100);
              setFileProgress((prev) => ({ ...prev, [index]: pct }));
            }
          },
        });
      });

      await Promise.all(uploadPromises);

      setFileProgress((prev) => {
        const complete = { ...prev };
        files.forEach((_, i) => { complete[i] = 100; });
        return complete;
      });

      const submissionPayload = { comp_id, description, files_metadata };
      const resp = await createSubmission(submissionPayload);

      if (typeof updateSubmissions === "function") {
        updateSubmissions(resp);
      }

      setIsSuccess(true);
      setFiles([]);
      setDescription("");
      setBlobUrls({});
      setLoadingStates({});
      setIsInvalid(false);
      setFileProgress({});
  
      } catch (err) {
  let msg = "An error occurred while uploading/submitting.";

  const status  = err?.response?.status;
  const data    = err?.response?.data;
  const dataStr = typeof data === "string" ? data : "";

  if (status === 400 && dataStr.includes("DATA_UPLOAD_MAX_MEMORY_SIZE")) {
    msg = "This file is too large for the local dev server. Add DATA_UPLOAD_MAX_MEMORY_SIZE to settings.py, or test uploads in staging.";
  } else if (status === 413) {
    msg = "File is too large. Please upload a smaller file.";
  } else if (data?.error) {
    msg = data.error;
  } else if (data && typeof data === "object") {
    const firstError = Object.values(data)[0];
    if (Array.isArray(firstError)) msg = firstError[0];
    else if (typeof firstError === "string") msg = firstError;
  } else if (err?.message) {
    msg = err.message;
  }

      setErrorMessage(msg);
      setFileProgress({});

      try {
        if (files_metadata.length > 0) {
          await Promise.all(files_metadata.map((f) => deleteFile(f.file_url)));
        }
      } catch {
        // ignore cleanup failures
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) closeModal(); }}>
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className="flex justify-center items-center"
      >
        <ModalCard title="Enter the Competition" onClose={() => closeModal()}>
          <div className="space-y-3 py-1">

            {/* ✅ Email verification warning banner — shown before user even tries to submit */}
            {!authUser?.email_verified && (
              <div className="flex items-start gap-2 rounded-lg border border-mikado-yellow/40 bg-mikado-yellow/10 px-3 py-2.5">
                <span className="text-mikado-yellow mt-0.5">⚠</span>
                <div>
                  <p className="text-xs font-medium text-mikado-yellow">
                    Email not verified
                  </p>
                  <p className="text-xs text-field-grey mt-0.5">
                    You won't be able to submit until you verify your email. Check your inbox for the verification link.
                  </p>
                </div>
              </div>
            )}

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-swan">Description</label>
              <Textarea
                placeholder="Describe your submission..."
                value={description}
                className="w-full h-24 border-collapse bg-transparent border-dark-grey text-field-grey"
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {/* Media Upload */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-swan">Media Upload</label>

              <div
                className="border-2 border-dashed border-dark-grey rounded-lg p-10 text-center"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
              >
                <div className="space-y-2">
                  {files.length > 0 ? (
                    <div className="flex flex-wrap gap-2 justify-center">
                      {files.map((file, index) => {
                        const previewFile = {
                          file_url: blobUrls[file.name] || URL.createObjectURL(file),
                          file_type: file.type ? file.type.split("/")[0] : "",
                        };

                        return (
                          <div key={`${file.name}-${index}`} className="relative w-24 h-24">
                            {loadingStates[index] && (
                              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                                <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
                              </div>
                            )}
                            <div
                              className="w-full h-full rounded-lg overflow-hidden"
                              style={{ display: loadingStates[index] ? "none" : "block" }}
                            >
                              <MediaPreview file={previewFile} thumbnail />
                            </div>
                            <img
                              src={previewFile.file_url}
                              alt=""
                              className="hidden"
                              onLoad={() => handleMediaLoad(index)}
                              onError={() => handleMediaLoad(index)}
                            />
                            <CloseIconButton
                              onClick={() => removeFile(index)}
                              className="absolute top-1 right-1"
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Drag & drop files here</p>
                  )}

                  <Button
                    variant="primary"
                    onClick={() => document.getElementById("file-input").click()}
                    disabled={isUploading}
                  >
                    Upload Files
                  </Button>

                  <input
                    id="file-input"
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={handleFileInput}
                  />
                </div>
              </div>

              {isInvalid && (
                <p className="mt-2 text-red-500">
                  Invalid file type detected. Only images and videos were added.
                </p>
              )}

              {isUploading && (
                <UploadProgressBar progress={uploadProgress} />
              )}

              <div>
                {errorMessage && (
                  <p className="mt-2 text-red-500">{errorMessage}</p>
                )}
              {isSuccess && (
                <p className="mt-2 text-mikado-yellow font-bold text-[13px]">
                  {competition?.auto_approve_submissions
                    ? 'CONGRATULATIONS! YOUR SUBMISSION IS LIVE! 🚀'
                    : 'Your submission has been sent for review! Check back in a bit! 🎉'}
                </p>
              )}
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-center mt-2 py-4">
              <Button
                variant="primary"
                onClick={handleSubmit}
                className="w-full"
                disabled={isUploading}
              >
                {isUploading ? "Submitting…" : "Participate Now!"}
              </Button>
            </div>
          </div>
        </ModalCard>
      </DialogContent>
    </Dialog>
  );
};

export default CompetitionEntryForm;